import { useState, useEffect, useRef } from "react";
import { useAuth } from "../../context/authContext";
import { useNavigate } from "react-router-dom";
import styles from "./ProfilePage.module.css";
import Button from "../../components/UI/Button/Button";

import StatusNotification from "../../components/UI/StatusNotification/StatusNotification";
import LoadingSpinner from "../../components/UI/LoadingSpinner/LoadingSpinner";
import DefaultUserImage from "../../assets/images/default-user.webp";
import AuthService from "../../services/auth-services";
import ProfileService from "../../services/profile-service";

export default function ProfilePage() {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [isDeletingImage, setIsDeletingImage] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [profileDataLoaded, setProfileDataLoaded] = useState(false);
  const [notification, setNotification] = useState({
    isVisible: false,
    isLoading: false,
    isSuccess: false,
    message: "",
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phoneNumber: "",
    company: "",
    address: {
      street: "",
      city: "",
      state: "",
      country: "",
      zip: "",
    },
  });

  useEffect(() => {
    const loadUserProfile = async () => {
      if (user && !profileDataLoaded) {
        try {
          const profileData = await AuthService.getUserProfile();

          if (updateUser) {
            updateUser({ ...user, ...profileData });
          }

          setFormData({
            name: profileData.name || "",
            email: profileData.email || "",
            phoneNumber: profileData.phoneNumber || "",
            company: profileData.company || "",
            address: {
              street: profileData.address?.street || "",
              city: profileData.address?.city || "",
              state: profileData.address?.state || "",
              country: profileData.address?.country || "",
              zip: profileData.address?.zip || "",
            },
          });
          setProfileDataLoaded(true);
        } catch {
          setFormData({
            name: user.name || "",
            email: user.email || "",
            phoneNumber: user.phoneNumber || "",
            company: user.company || "",
            address: {
              street: user.address?.street || "",
              city: user.address?.city || "",
              state: user.address?.state || "",
              country: user.address?.country || "",
              zip: user.address?.zip || "",
            },
          });
          setProfileDataLoaded(true);
        }
      } else if (user && profileDataLoaded) {
        setFormData({
          name: user.name || "",
          email: user.email || "",
          phoneNumber: user.phoneNumber || "",
          company: user.company || "",
          address: {
            street: user.address?.street || "",
            city: user.address?.city || "",
            state: user.address?.state || "",
            country: user.address?.country || "",
            zip: user.address?.zip || "",
          },
        });
      } else {
        setFormData({
          name: "",
          email: "",
          phoneNumber: "",
          company: "",
          address: {
            street: "",
            city: "",
            state: "",
            country: "",
            zip: "",
          },
        });
        setProfileDataLoaded(false);
      }
      setIsLoading(false);
    };

    loadUserProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, profileDataLoaded]);

  useEffect(() => {
    setProfileDataLoaded(false);
  }, [user?.id]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name.startsWith("address.")) {
      const addressField = name.split(".")[1];
      setFormData((prev) => ({
        ...prev,
        address: {
          ...prev.address,
          [addressField]: value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    showNotification("Updating profile...");

    try {
      const updateData = {
        ...formData,
      };

      if (!isGoogleUser && passwordData.newPassword) {
        if (passwordData.newPassword !== passwordData.confirmPassword) {
          hideNotification();
          alert("New passwords do not match");
          setIsSaving(false);
          return;
        }

        if (passwordData.newPassword.length < 6) {
          hideNotification();
          alert("New password must be at least 6 characters long");
          setIsSaving(false);
          return;
        }

        await AuthService.updatePassword(
          passwordData.currentPassword,
          passwordData.newPassword
        );

        setPasswordData({
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
        });
      }

      const result = await ProfileService.updateProfile(updateData);

      if (updateUser) {
        updateUser({ ...user, ...result });
      }

      setFormData({
        name: result.name || "",
        email: result.email || "",
        phoneNumber: result.phoneNumber || "",
        company: result.company || "",
        address: {
          street: result.address?.street || "",
          city: result.address?.city || "",
          state: result.address?.state || "",
          country: result.address?.country || "",
          zip: result.address?.zip || "",
        },
      });

      showSuccess("Profile updated successfully!");
    } catch (error) {
      hideNotification();
      alert(error.message || "Failed to update profile. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    navigate("/settings");
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const allowedTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/webp",
      "image/gif",
    ];
    if (!allowedTypes.includes(file.type)) {
      alert("Please select a valid image file (JPEG, PNG, WebP, or GIF)");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert("File size must be less than 5MB");
      return;
    }

    setIsUploadingImage(true);
    setUploadSuccess(false);

    try {
      const result = await ProfileService.uploadProfileImage(file);

      if (result.success) {
        if (updateUser) {
          updateUser({ ...user, profileImage: result.data.profileImageUrl });
        }
        setUploadSuccess(true);
      }
    } catch (error) {
      hideNotification();
      alert(
        error.message || "Failed to upload profile image. Please try again."
      );
    } finally {
      setIsUploadingImage(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleChangeImageClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleDeleteImage = async () => {
    if (!user.profileImage) return;

    setIsDeletingImage(true);

    try {
      const result = await ProfileService.deleteProfileImage();

      if (result.success) {
        if (updateUser) {
          updateUser({ ...user, profileImage: "" });
        }
      }
    } catch (error) {
      hideNotification();
      alert(
        error.message || "Failed to delete profile image. Please try again."
      );
    } finally {
      setIsDeletingImage(false);
    }
  };

  const isGoogleUser = user?.googleId;

  const showNotification = (message, isLoading = true) => {
    setNotification({
      isVisible: true,
      isLoading,
      isSuccess: false,
      message,
    });
  };

  const showSuccess = (message) => {
    setNotification({
      isVisible: true,
      isLoading: false,
      isSuccess: true,
      message,
    });
  };

  const handleGoHome = () => {
    hideNotification();
    navigate("/");
  };

  const hideNotification = () => {
    setNotification({
      isVisible: false,
      isLoading: false,
      isSuccess: false,
      message: "",
    });
  };

  if (!user || isLoading) {
    return (
      <div className={styles.profilePage}>
        <div className={styles.container}>
          <div className={styles.loadingMessage}>
            <LoadingSpinner />
            Loading user data...
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={styles.profilePage}>
        <div className={styles.container}>
          <div className={styles.header}>
            <button
              type="button"
              onClick={handleCancel}
              className={styles.backButton}
              aria-label="Go back to settings"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden
              >
                <path
                  d="M19 12H5M12 19L5 12L12 5"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Back to Settings
            </button>
            <h1 className={styles.title}>Edit Profile</h1>
            <p className={styles.subtitle}>
              Update your personal information and profile details
            </p>
          </div>

          <div className={styles.profileCard}>
            <div className={styles.profileImageSection}>
              <div className={styles.profileImageContainer}>
                <div className={styles.profileImageWrapper}>
                  <img
                    src={user.profileImage || DefaultUserImage}
                    referrerPolicy="no-referrer"
                    alt={user.name || "User"}
                    className={`${styles.profileImage} ${
                      isUploadingImage || isDeletingImage
                        ? styles.profileImageLoading
                        : ""
                    }`}
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = DefaultUserImage;
                    }}
                  />
                  {(isUploadingImage || isDeletingImage) && (
                    <div className={styles.imageLoadingOverlay}>
                      <LoadingSpinner />
                    </div>
                  )}
                  {user.profileImage && !isGoogleUser ? (
                    <button
                      type="button"
                      className={`${styles.deleteImageButton} ${
                        isUploadingImage || isDeletingImage
                          ? styles.deleteImageButtonLoading
                          : ""
                      }`}
                      onClick={handleDeleteImage}
                      disabled={isUploadingImage || isDeletingImage}
                      title="Delete profile image"
                      aria-label="Delete profile image"
                    >
                      {!(isUploadingImage || isDeletingImage) ? (
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          aria-hidden
                        >
                          <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6h14zM10 11v6M14 11v6" />
                        </svg>
                      ) : null}
                    </button>
                  ) : null}
                </div>
                {!isGoogleUser ? (
                  <div className={styles.changePhotoContainer}>
                    <button
                      type="button"
                      className={`${styles.changePhotoButton} ${
                        isUploadingImage || isDeletingImage
                          ? styles.changePhotoButtonLoading
                          : ""
                      }`}
                      onClick={handleChangeImageClick}
                      disabled={isUploadingImage || isDeletingImage}
                    >
                      {isUploadingImage
                        ? "Uploading..."
                        : isDeletingImage
                        ? "Deleting..."
                        : "Change Photo"}
                    </button>
                    {uploadSuccess && (
                      <div className={styles.successCheckmark}>
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          aria-hidden
                        >
                          <path
                            d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                            stroke="#10b981"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                    )}
                  </div>
                ) : null}
                {isGoogleUser ? (
                  <div className={styles.googleUserMessage}>
                    <p className={styles.helpText}>
                      Profile image is managed by your Google account
                    </p>
                  </div>
                ) : null}
                {!isGoogleUser ? (
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
                    onChange={handleImageUpload}
                    className={styles.hiddenFileInput}
                  />
                ) : null}
              </div>
            </div>

            <form onSubmit={handleSubmit} className={styles.profileForm}>
              <div className={styles.formSection}>
                <h3 className={styles.sectionTitle}>Basic Information</h3>
                <div className={styles.formGrid}>
                  <div className={styles.formGroup}>
                    <label htmlFor="name" className={styles.label}>
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className={styles.input}
                      placeholder="Enter your full name"
                      required
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label htmlFor="email" className={styles.label}>
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className={styles.input}
                      required
                      disabled
                    />
                    <p className={styles.helpText}>
                      Email cannot be changed as it&apos;s linked to your Google
                      account
                    </p>
                  </div>
                  <div className={styles.formGroup}>
                    <label htmlFor="phoneNumber" className={styles.label}>
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="phoneNumber"
                      name="phoneNumber"
                      value={formData.phoneNumber}
                      onChange={handleInputChange}
                      className={styles.input}
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>
                </div>
              </div>

              <div className={styles.formSection}>
                <h3 className={styles.sectionTitle}>
                  Professional Information
                </h3>
                <div className={styles.formGrid}>
                  <div className={styles.formGroup}>
                    <label htmlFor="company" className={styles.label}>
                      Company
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      className={styles.input}
                      placeholder="Your company name"
                    />
                  </div>
                </div>
              </div>

              <div className={styles.formSection}>
                <h3 className={styles.sectionTitle}>Address Information</h3>
                <div className={styles.formGrid}>
                  <div className={styles.formGroup}>
                    <label htmlFor="address.street" className={styles.label}>
                      Street Address
                    </label>
                    <input
                      type="text"
                      id="address.street"
                      name="address.street"
                      value={formData.address.street}
                      onChange={handleInputChange}
                      className={styles.input}
                      placeholder="123 Main Street"
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label htmlFor="address.city" className={styles.label}>
                      City
                    </label>
                    <input
                      type="text"
                      id="address.city"
                      name="address.city"
                      value={formData.address.city}
                      onChange={handleInputChange}
                      className={styles.input}
                      placeholder="New York"
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label htmlFor="address.state" className={styles.label}>
                      State/Province
                    </label>
                    <input
                      type="text"
                      id="address.state"
                      name="address.state"
                      value={formData.address.state}
                      onChange={handleInputChange}
                      className={styles.input}
                      placeholder="NY"
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label htmlFor="address.country" className={styles.label}>
                      Country
                    </label>
                    <input
                      type="text"
                      id="address.country"
                      name="address.country"
                      value={formData.address.country}
                      onChange={handleInputChange}
                      className={styles.input}
                      placeholder="United States"
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label htmlFor="address.zip" className={styles.label}>
                      ZIP/Postal Code
                    </label>
                    <input
                      type="text"
                      id="address.zip"
                      name="address.zip"
                      value={formData.address.zip}
                      onChange={handleInputChange}
                      className={styles.input}
                      placeholder="10001"
                    />
                  </div>
                </div>
              </div>

              {!isGoogleUser ? (
                <div className={styles.formSection}>
                  <h3 className={styles.sectionTitle}>Change Password</h3>
                  <div className={styles.formGrid}>
                    <div className={styles.formGroup}>
                      <label htmlFor="currentPassword" className={styles.label}>
                        Current Password
                      </label>
                      <input
                        type="password"
                        id="currentPassword"
                        name="currentPassword"
                        value={passwordData.currentPassword}
                        onChange={handlePasswordChange}
                        className={styles.input}
                        placeholder="Enter your current password"
                      />
                    </div>
                    <div className={styles.formGroup}>
                      <label htmlFor="newPassword" className={styles.label}>
                        New Password
                      </label>
                      <input
                        type="password"
                        id="newPassword"
                        name="newPassword"
                        value={passwordData.newPassword}
                        onChange={handlePasswordChange}
                        className={styles.input}
                        placeholder="Enter your new password"
                        minLength={6}
                      />
                      <p className={styles.helpText}>
                        Password must be at least 6 characters long
                      </p>
                    </div>
                    <div className={styles.formGroup}>
                      <label htmlFor="confirmPassword" className={styles.label}>
                        Confirm New Password
                      </label>
                      <input
                        type="password"
                        id="confirmPassword"
                        name="confirmPassword"
                        value={passwordData.confirmPassword}
                        onChange={handlePasswordChange}
                        className={styles.input}
                        placeholder="Confirm your new password"
                        minLength={6}
                      />
                    </div>
                  </div>
                </div>
              ) : null}

              <div className={styles.formActions}>
                <Button type="button" variant="outlined" onClick={handleCancel}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isSaving}>
                  {isSaving ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <StatusNotification
        isVisible={notification.isVisible}
        isLoading={notification.isLoading}
        isSuccess={notification.isSuccess}
        message={notification.message}
        onComplete={hideNotification}
        autoHide={!notification.isSuccess}
        hideDelay={2000}
        successButton={
          notification.isSuccess ? (
            <Button type="button" onClick={handleGoHome}>
              Go to Home
            </Button>
          ) : null
        }
      />
    </>
  );
}
